import {
  Divider,
  Box,
  Button,
  Card,
  CardHeader,
  Container,
  Grid,
  Skeleton,
  Typography,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import { Outlet, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TabPanel from '../../../../components/molecules/tab-panel';
import { HostsTableSimple } from '../../../../components/organisms/common/hosts-table-simple';
import { ArrowLeft as ArrowLeftIcon } from '../../../../icons/arrow-left';
import { ExclamationOutlined as ExclamationOutlinedIcon } from '../../../../icons/exclamation-outlined';
import { useBillingGroup } from '../../hooks/query/use-billing-group';
import { useDiscountHistory } from '../../hooks/query/use-discount-history';
import { DiscountInfo } from '../molecules/discount-info';
import { Info } from '../molecules/info';
import RemoveBillingGroupDialog from '../organisms/dialogs/remove-billing-group-dialog';
import { DiscountHistoryTable } from '../organisms/discount-history-table';

import { Pagination } from '@/components/organisms/pagination';
import { useHostsWithSearch } from '@/features/hosts/hooks/query/use-hosts-with-search';
import { useDialog } from '@/hooks/use-dialog';
import { useNavigateBack } from '@/hooks/use-navigate-back';
import { usePagination } from '@/hooks/use-pagination';
import { Can, withPermissions } from '@/permissions/can';

const BillingGroup = () => {
  const { t } = useTranslation(['billingGroups', 'common', 'hosts', 'host']);

  const { navigateBack } = useNavigateBack();

  const navigate = useNavigate({ from: '/billing-groups/$billingGroupId' });

  const [activeTab, setActiveTab] = useState(0);

  const billingGroupId = useParams({
    from: '/_layout/billing-groups/$billingGroupId',
    select: state => state.billingGroupId,
  });

  const [
    isOpenRemoveBillingGroupDialog,
    handleOpenRemoveBillingGroupDialog,
    handleCloseRemoveBillingGroupDialog,
  ] = useDialog();

  const {
    changePage: hostsChangePage,
    changePerPage: hostsChangePerPage,
    page: hostsPage,
    perPage: hostsPerPage,
    setTotalItems: hostsSetTotalItems,
    totalPages: hostsTotalPages,
  } = usePagination();

  const {
    data: hostsData,
    isFetching: isFetchingHosts,
    isLoading: isLoadingHosts,
    error: hostsError,
  } = useHostsWithSearch({ page: hostsPage, perPage: hostsPerPage, billingGroupId });

  const parsedBillingGroupId = billingGroupId ? +billingGroupId : 0;

  const { data, isLoading, error } = useBillingGroup(+parsedBillingGroupId);

  const {
    changePage: discountHistoryChangePage,
    changePerPage: discountHistoryChangePerPage,
    page: discountHistoryPage,
    perPage: discountHistoryPerPage,
    setTotalItems: discountHistorySetTotalItems,
    totalPages: discountHistoryTotalPages,
  } = usePagination();

  const {
    data: discountHistoryData,
    isLoading: isLoadingDiscountHistory,
    isFetching: isFetchingDiscountHistory,
    error: discountHistoryError,
  } = useDiscountHistory({
    id: parsedBillingGroupId,
    page: discountHistoryPage,
    perPage: discountHistoryPerPage,
  });

  const tabs = [
    {
      label: t('common:information'),
    },
    {
      label: t('hosts:title'),
    },
  ];

  useEffect(() => {
    if (hostsData) {
      hostsSetTotalItems(hostsData.total);
    }
  }, [hostsData]);

  useEffect(() => {
    if (discountHistoryData) {
      discountHistorySetTotalItems(discountHistoryData.total);
    }
  }, [discountHistoryData]);

  const renderContent = () => {
    if (isLoading || isLoadingDiscountHistory) {
      return (
        <Box sx={{ py: 4 }}>
          <Skeleton height={42} />
          <Skeleton />
          <Skeleton />
        </Box>
      );
    }

    if (!data || error) {
      return (
        <Box sx={{ py: 4 }}>
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: 'background.default',
              display: 'flex',
              flexDirection: 'column',
              p: 3,
            }}>
            <ExclamationOutlinedIcon />
            <Typography color="textSecondary" sx={{ mt: 2 }} variant="body2">
              {error?.response?.data?.message} {discountHistoryError?.response?.data?.message}
            </Typography>
          </Box>
        </Box>
      );
    }

    const navigateToBillingGroupForm = () => {
      navigate({
        to: '/billing-groups/edit',
        search: {
          groupName: data?.name,
          discountTemplates: data?.discountTemplates || [],
          id: data?.id,
        },
      });
    };

    return (
      <>
        <Box sx={{ py: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Button
              color="primary"
              startIcon={<ArrowLeftIcon />}
              onClick={() => navigateBack('/billing-groups')}
              variant="text">
              {t('host:back')}
            </Button>
          </Box>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
            }}>
            <Typography color="textPrimary" variant="h4">
              {data?.name}
            </Typography>
            <Box>
              <Can I="manageParkingDiscounts" a="BillingGroups" passThrough>
                {allowed => (
                  <Tooltip
                    title={t('common:notSufficientPermissions')}
                    disableHoverListener={allowed}
                    disableFocusListener={allowed}>
                    <span>
                      <Button
                        sx={{ mr: 2 }}
                        onClick={navigateToBillingGroupForm}
                        size="large"
                        disabled={!allowed}
                        variant="outlined">
                        {t('billingGroups:edit')}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Can>
              <Can I="delete" a="BillingGroups" passThrough>
                {allowed => (
                  <Tooltip
                    title={t('common:notSufficientPermissions')}
                    disableHoverListener={allowed}
                    disableFocusListener={allowed}>
                    <span>
                      <Button
                        onClick={handleOpenRemoveBillingGroupDialog}
                        size="large"
                        color="error"
                        disabled={!allowed}
                        variant="outlined">
                        {t('billingGroups:delete')}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Can>
            </Box>
          </Box>
          <Tabs
            allowScrollButtonsMobile
            value={activeTab}
            variant="scrollable"
            onChange={(e, newValue) => setActiveTab(newValue)}>
            {tabs.map(option => (
              <Tab key={option.label} label={option.label} />
            ))}
          </Tabs>
          <Divider sx={{ mb: 2 }} />
          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={3}>
              <Grid
                container
                item
                lg={6}
                spacing={3}
                sx={{
                  height: 'fit-content',
                  order: {
                    md: 2,
                    xs: 1,
                  },
                }}
                xs={12}>
                <Grid item xs={12}>
                  <Info name={data?.name} />
                </Grid>
              </Grid>
              <Grid
                container
                item
                lg={6}
                spacing={3}
                sx={{
                  height: 'fit-content',
                  order: {
                    md: 2,
                    xs: 1,
                  },
                }}
                xs={12}>
                <Grid item xs={12}>
                  <DiscountInfo discountTemplates={data?.discountTemplates || []} />
                </Grid>
              </Grid>
              <Grid
                container
                item
                lg={12}
                spacing={3}
                sx={{
                  height: 'fit-content',
                  order: {
                    md: 2,
                    xs: 1,
                  },
                }}
                xs={12}>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardHeader title={t('billingGroups:tableHistory.cardTitle')} />
                    <Divider />
                    <DiscountHistoryTable
                      data={discountHistoryData?.resources || []}
                      isLoading={isLoadingDiscountHistory}
                      error={discountHistoryError?.response?.data?.message || ''}
                    />
                    <Divider sx={{ mt: 'auto' }} />
                    <Pagination
                      changePage={discountHistoryChangePage}
                      page={discountHistoryPage}
                      perPage={discountHistoryPerPage}
                      changePerPage={discountHistoryChangePerPage}
                      isFetching={isFetchingDiscountHistory}
                      totalPages={discountHistoryTotalPages}
                    />
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <Grid
              container
              item
              lg={12}
              spacing={3}
              sx={{
                height: 'fit-content',
                order: {
                  md: 2,
                  xs: 1,
                },
              }}
              xs={12}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardHeader title={t('billingGroups:host.cardTitle')} />
                  <Divider />
                  <HostsTableSimple
                    hosts={hostsData?.resources || []}
                    isLoading={isLoadingHosts}
                    error={hostsError?.response?.data?.message || ''}
                  />
                  <Divider sx={{ mt: 'auto' }} />
                  <Pagination
                    changePage={hostsChangePage}
                    page={hostsPage}
                    perPage={hostsPerPage}
                    changePerPage={hostsChangePerPage}
                    isFetching={isFetchingHosts}
                    totalPages={hostsTotalPages}
                  />
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
        <Outlet />
      </>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        flexGrow: 1,
      }}>
      <Container
        maxWidth="lg"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}>
        {renderContent()}
      </Container>
      <RemoveBillingGroupDialog
        billingGroupId={data?.id || 0}
        billingGroupName={data?.name || ''}
        open={isOpenRemoveBillingGroupDialog}
        onCancel={handleCloseRemoveBillingGroupDialog}
      />
    </Box>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'BillingGroups'),
  BillingGroup,
);
